import { useState, useMemo } from "react";
import {
  Card,
  BlockStack,
  Text,
  EmptyState,
  Button,
  Box,
  InlineStack,
  InlineGrid,
  SortableList,
  SortableResourceItem,
} from "@medentic/polaris-react";
import {
  PlusIcon,
  XIcon,
  PlusCircleIcon,
} from "@medentic/polaris-icons";
import { VisibilityIcon } from "@features/events/components/EventBadgeRenderer";
import { useTranslation } from "react-i18next";

import { Event, Brand } from "@features/events/types";
import { useQuery } from "@tanstack/react-query";
import useApiRequest from "@shared/hooks/common/useApiRequest";
import { useParams } from "react-router-dom";
import AssignBrandsModal from "@features/events/components/AssignBrandsModal";
import EventsDefaultAvatar from "@features/events/components/EventsDefaultAvatar";
import { getLocalizedText } from "@shared/lib";
import emptyBrandsImage from "@shared/assets/empty-states/empty-brands.svg";

interface PartnerBrandsCardProps {
  eventData: Partial<Event>;
  onDataChange: (updates: Partial<Event>) => void;
}

interface BrandItemProps {
  brand: Brand;
  onRemove: (brandId: string) => void;
}

const BrandItem: React.FC<BrandItemProps> = ({
  brand,
  onRemove,
}) => {
  const { t, } = useTranslation();
  const [isHovered, setIsHovered] = useState(false);

  return (
    <SortableResourceItem
      accessibilityLabel={`View details for ${getLocalizedText(brand.name) || "brand"}`}
      dragHandleAccessibilityLabel={`Reorder ${getLocalizedText(brand.name) || "brand"}`}
    >
      <div
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <InlineGrid columns={["twoThirds", "oneThird"]}>
          <InlineStack gap="200" blockAlign="center" wrap={false}>
            <EventsDefaultAvatar
              name={brand.name}
              singleImage={brand.logoSquare}
            />
            <BlockStack gap="100">
              <InlineStack gap="200" align="start">
                <Text
                  as="span"
                  variant="bodyMd"
                  fontWeight={brand.visibility === 1 ? "medium" : "regular"}
                  tone={brand.visibility === 1 ? undefined : "subdued"}
                >
                  {getLocalizedText(brand.name)}
                </Text>
                <VisibilityIcon visibility={brand.visibility} />
              </InlineStack>
              <Box maxWidth="100%">
                <Text as="span" variant="bodySm" tone="subdued" truncate>
                  {getLocalizedText(brand.headline)}
                </Text>
              </Box>
            </BlockStack>
          </InlineStack>

          <InlineStack
            gap="300"
            align="space-between"
            blockAlign="center"
            wrap={false}
          >
            <Text as="p" variant="bodyMd" tone="subdued">
              {getLocalizedText(brand.geoAddress?.summary)}
            </Text>
            <Box opacity={isHovered ? "1" : "0"} paddingInlineEnd="100">
              <InlineStack gap="200" blockAlign="center" align="end">
                <Button
                  icon={XIcon}
                  tone="critical"
                  variant="tertiary"
                  onClick={() => onRemove(brand.id)}
                  accessibilityLabel={t(
                    "eventDetails.partnerBrands.removeBrand"
                  )}
                />
              </InlineStack>
            </Box>
          </InlineStack>
        </InlineGrid>
      </div>
    </SortableResourceItem>
  );
};

export default function PartnerBrandsCard({
  eventData,
  onDataChange,
}: PartnerBrandsCardProps) {
  const { t } = useTranslation();
  const { eventOrganizerId: hcc } = useParams<{ eventOrganizerId: string }>();
  const { GET } = useApiRequest();
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Fetch all brands to get brand details for assigned brandIds
  const { data: allBrands = [] } = useQuery<Brand[]>({
    queryKey: ["brands", hcc],
    queryFn: () => GET(`/api/healthcare-companies/${hcc}/e/brands`),
    enabled: Boolean(hcc),
  });

  const assignedBrands = useMemo(() => {
    if (!eventData.brandIds || eventData.brandIds.length === 0) return [];
    if (allBrands.length === 0) return [];
    return eventData.brandIds
      .map((brandId) => allBrands.find((brand) => brand.id === brandId))
      .filter((brand): brand is Brand => brand !== undefined);
  }, [eventData.brandIds, allBrands]);

  const handleAddBrands = () => {
    setIsModalOpen(true);
  };

  const handleAssignBrands = (brandIds: string[]) => {
    // In real app, fetch brand details from API
    onDataChange({ brandIds });
    setIsModalOpen(false);
  };

  const handleRemoveBrand = (brandId: string) => {
    const updatedBrandIds = (eventData.brandIds || []).filter(
      (id: string) => id !== brandId
    );
    // Update parent data - the useEffect will handle updating local state
    onDataChange({ brandIds: updatedBrandIds });
  };

  const renderBrandItem = (brand: Brand) => (
    <BrandItem
      brand={brand}
      onRemove={handleRemoveBrand}
    />
  );

  const hasBrands = assignedBrands.length > 0;

  return (
    <>
      <Card>
        <BlockStack gap="400">
          <InlineStack align="space-between" blockAlign="center">
            <BlockStack gap="100">
              <Text as="h2" variant="headingMd">
                {t("eventDetails.partnerBrands.title")}
              </Text>
              <Text as="p" variant="bodyMd" tone="subdued">
                {t("eventDetails.partnerBrands.description")}
              </Text>
            </BlockStack>
          </InlineStack>

          {hasBrands ? (
            <BlockStack gap="200">
              <Box
                borderColor="border"
                borderWidth="025"
                borderRadius="200"
                background="bg-surface"
              >
                <SortableList
                  items={assignedBrands}
                  onChange={(reorderedBrands) => {
                    // Update parent data - useMemo will recalculate assignedBrands
                    onDataChange({
                      brandIds: reorderedBrands.map((brand) => brand.id),
                    });
                  }}
                  idForItem={(brand) => brand?.id}
                  renderItem={(item) => renderBrandItem(item)}
                  showDividers={true}
                />
              </Box>
              {hasBrands && (
                <InlineStack>
                  <Button
                    variant="plain"
                    icon={PlusCircleIcon}
                    onClick={handleAddBrands}
                  >
                    {t("eventDetails.partnerBrands.addButton")}
                  </Button>
                </InlineStack>
              )}
            </BlockStack>
          ) : (
            <EmptyState
              heading={t("eventDetails.partnerBrands.emptyState")}
              action={{
                content: t("eventDetails.partnerBrands.addButton"),
                icon: PlusIcon,
                onAction: handleAddBrands,
              }}
              image={emptyBrandsImage}
            />
          )}
        </BlockStack>
      </Card>

      <AssignBrandsModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        selectedBrandIds={eventData.brandIds || []}
        onAssign={handleAssignBrands}
      />
    </>
  );
}
